// src/utils/dateFormatter.js

/**
 * Format a pure relative time string (e.g. "2w ago", "3d ago", "2h ago", "yesterday", "just now")
 * without sentence prefixes like "Listed" or "Ajouté".
 */
export const formatTimeAgo = (dateString, language = "en") => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.max(0, Math.floor(diffMs / 1000));
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  const isFr = language && language.toLowerCase().startsWith("fr");

  if (diffDays <= 0) {
    if (diffHours < 1) {
      if (diffMins <= 2) {
        return isFr ? "à l'instant" : "just now";
      }
      return isFr ? `${diffMins}m` : `${diffMins}m ago`;
    }
    return isFr ? `${diffHours}h` : `${diffHours}h ago`;
  }

  if (diffDays === 1) {
    return isFr ? "hier" : "yesterday";
  }

  if (diffDays < 7) {
    return isFr ? `${diffDays}j` : `${diffDays}d ago`;
  }

  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    if (weeks === 1) {
      return isFr ? "1 sem." : "1w ago";
    }
    return isFr ? `${weeks} sem.` : `${weeks}w ago`;
  }

  const formattedDate = date.toLocaleDateString(isFr ? "fr-FR" : "en-US", {
    day: "numeric",
    month: "short",
  });
  return formattedDate;
};

/**
 * Format a full sentence date string for product listing dates on Product Detail Page.
 * e.g. "Listed today", "Listed 2 days ago", "Listed 2 weeks ago", "Listed on 27 Aug 2026"
 * or French: "Ajouté aujourd'hui", "Ajouté il y a 2 jours", "Ajouté le 27 août 2026"
 */
export const formatProductAddedDate = (dateString, language = "en") => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.max(0, Math.floor(diffMs / 1000));
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  const isFr = language && language.toLowerCase().startsWith("fr");

  if (diffDays <= 0) {
    if (diffHours < 1) {
      if (diffMins <= 2) {
        return isFr ? "Ajouté à l'instant" : "Listed just now";
      }
      return isFr ? `Ajouté il y a ${diffMins} min` : `Listed ${diffMins}m ago`;
    }
    return isFr ? `Ajouté il y a ${diffHours}h` : `Listed ${diffHours}h ago`;
  }

  if (diffDays === 1) {
    return isFr ? "Ajouté hier" : "Listed yesterday";
  }

  if (diffDays < 7) {
    return isFr ? `Ajouté il y a ${diffDays} jours` : `Listed ${diffDays} days ago`;
  }

  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    if (weeks === 1) {
      return isFr ? "Ajouté il y a 1 sem." : "Listed 1 week ago";
    }
    return isFr ? `Ajouté il y a ${weeks} sem.` : `Listed ${weeks} weeks ago`;
  }

  // Older than a month: format as e.g. "Listed 27 Aug 2026"
  const formattedDate = date.toLocaleDateString(isFr ? "fr-FR" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return isFr ? `Ajouté le ${formattedDate}` : `Listed on ${formattedDate}`;
};

/**
 * Format a full localized date string for technical specifications table.
 * e.g. "27 August 2026" or "27 août 2026"
 */
export const formatProductFullDate = (dateString, language = "en") => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";

  const isFr = language && language.toLowerCase().startsWith("fr");
  return date.toLocaleDateString(isFr ? "fr-FR" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};
